/**
 * 71.5 step 1 (ARCHITECTURE §5.36 (6), *The shaping record*; the coordinator, Oct 29): the one rule that shapes the ground and
 * where a point stands against a record's footprint. A record (`Shaping`, types/ground.ts) is a **pad** or a **ribbon**, in
 * `set`, `raise` or `lower`, with its own batters and a reach. The rule holds the ground `g` to clamp(g, L − fill·d, L + cut·d):
 * `d` the distance outside the footprint (0 inside, where `set` is `L` flat to the bit), `L` the footprint's level there,
 * untouched at `reach` and beyond. A terrace is a pad in `set` (core/terraceGround.ts terracePad), and its rule is this one,
 * bit for bit — the step's test. Nothing lays a ribbon yet (roads 71.6, §5.39 step 3's passes and defiles are later steps);
 * dents and log order are step 2.
 *
 * Pure; the frames are dot products (the chord for the arc at these sizes, as a terrace's frame), no trig.
 */
import type { Shaping, ShapingMode, ShapingNode, Vec3 } from '../types';

/**
 * The rule: ground `g` at distance `d` outside a footprint whose level there is `L`. `set` cuts and fills, `raise` fills only,
 * `lower` cuts only. Written as the two one-sided bounds (the clamp, since L − fill·d ≤ L ≤ L + cut·d) in the order a
 * terrace's rule always took them, so a terrace's ground is the same to the bit.
 */
export function shapingRule(mode: ShapingMode, cut: number, fill: number, reach: number, d: number, L: number, g: number): number {
  if (d === 0) return mode === 'set' ? L : mode === 'raise' ? Math.max(g, L) : Math.min(g, L);
  if (d >= reach) return g;
  if (mode === 'set') return g > L ? Math.min(g, L + cut * d) : Math.max(g, L - fill * d);
  return mode === 'raise' ? Math.max(g, L - fill * d) : Math.min(g, L + cut * d);
}

/** A point (x across, z along a pad's `facing`; world units from its centre) — how far outside a `halfX` × `halfZ` pad (0 inside). */
export function padDistance(halfX: number, halfZ: number, x: number, z: number): number {
  const dx = Math.max(Math.abs(x) - halfX, 0), dz = Math.max(Math.abs(z) - halfZ, 0);
  return dx === 0 && dz === 0 ? 0 : Math.sqrt(dx * dx + dz * dz);
}

/** A unit direction's place in a pad's frame (world units from `dir`; x across `facing`, z along it), into `out` [x, z]. */
export function padLocal(dir: Vec3, facing: Vec3, radius: number, ux: number, uy: number, uz: number, out: Float64Array): void {
  const ox = ux - dir.x, oy = uy - dir.y, oz = uz - dir.z;
  const rx = facing.y * dir.z - facing.z * dir.y, ry = facing.z * dir.x - facing.x * dir.z, rz = facing.x * dir.y - facing.y * dir.x;   // across the pad (x), as a dig's frame
  out[0] = (ox * rx + oy * ry + oz * rz) * radius;
  out[1] = (ox * facing.x + oy * facing.y + oz * facing.z) * radius;
}

/**
 * A unit direction against a ribbon (`nodes` along its spine, `half` across): into `out` [d, L] — its distance outside the
 * ribbon (0 inside; square ends) and the spine's level at its nearest point (the nodes' levels, linear along each segment).
 * The nearest segment wins; a shared node joins two segments without a gap.
 */
export function ribbonPlace(nodes: ReadonlyArray<ShapingNode>, half: number, radius: number, ux: number, uy: number, uz: number, out: Float64Array): void {
  let bestD = Infinity, bestL = nodes.length ? nodes[0].level : 0;
  for (let k = 0; k + 1 < nodes.length; k++) {
    const a = nodes[k].dir, b = nodes[k + 1].dir;
    const ex = b.x - a.x, ey = b.y - a.y, ez = b.z - a.z, len2 = ex * ex + ey * ey + ez * ez;
    if (len2 === 0) continue;
    const len = Math.sqrt(len2);
    const px = ux - a.x, py = uy - a.y, pz = uz - a.z;
    const t = (px * ex + py * ey + pz * ez) / len2;                         // along the segment, 0 … 1 between its nodes
    const ax = px - t * ex, ay = py - t * ey, az = pz - t * ez;             // across it
    const across = Math.sqrt(ax * ax + ay * ay + az * az) * radius;
    const dAlong = (t < 0 ? -t : t > 1 ? t - 1 : 0) * len * radius;       // past a square end
    const dAcross = Math.max(across - half, 0);
    const d = dAlong === 0 && dAcross === 0 ? 0 : Math.sqrt(dAlong * dAlong + dAcross * dAcross);
    if (d < bestD) {
      const tc = t < 0 ? 0 : t > 1 ? 1 : t;
      bestD = d; bestL = nodes[k].level + (nodes[k + 1].level - nodes[k].level) * tc;
    }
  }
  out[0] = bestD; out[1] = bestL;
}

const place = new Float64Array(2);
/** The ground record `s` makes of `g` at the unit direction (ux, uy, uz) on a planet of `radius`. */
export function shapedAt(s: Shaping, radius: number, ux: number, uy: number, uz: number, g: number): number {
  const f = s.footprint;
  let d: number, L: number;
  if (f.kind === 'pad') {
    padLocal(f.dir, f.facing, radius, ux, uy, uz, place);
    d = padDistance(f.halfX, f.halfZ, place[0], place[1]); L = f.level;
  } else {
    ribbonPlace(f.nodes, f.half, radius, ux, uy, uz, place);
    d = place[0]; L = place[1];
  }
  return shapingRule(s.mode, s.cut, s.fill, s.reach, d, L, g);
}
